import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import SharedHelmet from "../Helmet";
const TITLE = "حاسبة وقت صلاة التهجد - حساب الثلث الأخير من الليل";
const DESCRIPTION =
    "حاسبة وقت صلاة التهجد هي أداة تساعدك على تحديد أفضل وقت لأداء صلاة التهجد، وهو الثلث الأخير من الليل. أدخل وقت صلاة المغرب ووقت صلاة الفجر لحساب بداية ونهاية هذا الوقت المبارك.";
const KEYWORDS =
    "حاسبة صلاة التهجد, وقت صلاة التهجد, حساب الثلث الأخير من الليل, كيفية حساب وقت التهجد, أفضل وقت لصلاة التهجد, حاسبة قيام الليل";
const OG_URL = "https://do-calculate.com/calculator/ar/tahajjud-time/";
function TimeInput({
    label,
    hour,
    setHour,
    minute,
    setMinute,
    period,
    setPeriod,
    timeFormat,
}) {
    const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
    const hours24 = Array.from({ length: 24 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    const minutes = Array.from({ length: 60 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    return (
        <div className="form-group mb-3">
            <b>
                <label className="form-label d-block mb-2">{label}</label>
            </b>
            <div className="d-flex align-items-center">
                <select
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                    className="form-select me-2"
                    style={{ flex: 1 }}
                >
                    {timeFormat === "12"
                        ? hours12.map((h) => (
                            <option key={h} value={h}>
                                {h}
                            </option>
                        ))
                        : hours24.map((h) => (
                            <option key={h} value={h}>
                                {h}
                            </option>
                        ))}
                </select>
                <span className="mx-1">:</span>
                <select
                    value={minute}
                    onChange={(e) => setMinute(e.target.value)}
                    className="form-select mx-2"
                    style={{ flex: 1 }}
                >
                    {minutes.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
                {timeFormat === "12" && (
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="form-select"
                        style={{ flex: 1 }}
                    >
                        <option value="AM">صباحًا</option>
                        <option value="PM">مساءً</option>
                    </select>
                )}
            </div>
        </div>
    );
}
function TahajjudCalculator() {
    const [timeFormat, setTimeFormat] = useState("12");
    const [maghribHour, setMaghribHour] = useState("6");
    const [maghribMinute, setMaghribMinute] = useState("30");
    const [maghribPeriod, setMaghribPeriod] = useState("PM");
    const [fajrHour, setFajrHour] = useState("4");
    const [fajrMinute, setFajrMinute] = useState("00");
    const [fajrPeriod, setFajrPeriod] = useState("AM");
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const calculateTahajjudTime = () => {
        setIsLoading(true);
        setError(null);
        setResult(null);
        const get24HourFormat = (hourStr, minuteStr, period) => {
            let hour = parseInt(hourStr, 10);
            if (timeFormat === "12") {
                if (period === "PM" && hour !== 12) {
                    hour += 12;
                }
                if (period === "AM" && hour === 12) {
                    hour = 0;
                }
            }
            return `${hour.toString().padStart(2, "0")}:${minuteStr}`;
        };
        const maghribTime24 = get24HourFormat(
            maghribHour,
            maghribMinute,
            maghribPeriod
        );
        const fajrTime24 = get24HourFormat(fajrHour, fajrMinute, fajrPeriod);
        const maghrib = new Date(`2024-01-01T${maghribTime24}:00`);
        const fajr = new Date(`2024-01-02T${fajrTime24}:00`);
        if (fajr <= maghrib) {
            setError("وقت الفجر يجب أن يكون بعد وقت المغرب.");
            setIsLoading(false);
            return;
        }
        const nightDurationInMs = fajr - maghrib;
        const oneThirdOfNightInMs = nightDurationInMs / 3;
        const lastThirdStartTime = new Date(fajr.getTime() - oneThirdOfNightInMs);
        const formatTimeForDisplay = (date) => {
            return date.toLocaleTimeString("ar-EG", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        };
        setResult({
            start: formatTimeForDisplay(lastThirdStartTime),
            end: formatTimeForDisplay(fajr),
        });
        setIsLoading(false);
    };
    return (
        <div className="rtl">
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
            />
            <div className="row">
                <div className="col-12 col-md-8">
                    <div
                        className="bg-success text-white text-center"
                        style={{ height: "50px", lineHeight: "50px" }}
                    >
                        <h1 style={{ fontSize: "17px" }}>حاسبة وقت صلاة التهجد</h1>
                    </div>
                    <div className="border border-light p-3">
                        <p>
                            تساعدك حاسبة الثلث الأخير من الليل على معرفة أفضل وقت لأداء صلاة التهجد، وهو من أعظم أوقات العبادة والدعاء المستجاب. تعتمد الأداة على حساب الوقت بين أذان المغرب وأذان الفجر لتحديد موعد بداية ونهاية الثلث الأخير من الليل بدقة. فقط أدخل توقيت أذان المغرب وأذان الفجر حسب منطقتك لتحصل على النتيجة الفورية. هذه الحاسبة مفيدة للمسلمين الباحثين عن طريقة سهلة لمعرفة وقت قيام الليل، خاصة في رمضان أو خلال الأيام الفاضلة.
                        </p>
                        <hr />
                        <div className="row">
                            <div className="col-sm-12 col-md-8 calculatorStyle">
                                <div className="p-3 mb-3 bg-success text-white">
                                    أدخل أوقات الصلاة
                                </div>
                                <div className="d-flex justify-content-center mb-3">
                                    <div className="btn-group" role="group">
                                        <button
                                            type="button"
                                            className={`btn ${timeFormat === "12" ? "btn-success" : "btn-outline-success"
                                                }`}
                                            onClick={() => setTimeFormat("12")}
                                        >
                                            تنسيق 12 ساعة
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn ${timeFormat === "24" ? "btn-success" : "btn-outline-success"
                                                }`}
                                            onClick={() => setTimeFormat("24")}
                                        >
                                            تنسيق 24 ساعة
                                        </button>
                                    </div>
                                </div>
                                <TimeInput
                                    label="وقت صلاة المغرب"
                                    hour={maghribHour}
                                    setHour={setMaghribHour}
                                    minute={maghribMinute}
                                    setMinute={setMaghribMinute}
                                    period={maghribPeriod}
                                    setPeriod={setMaghribPeriod}
                                    timeFormat={timeFormat}
                                />
                                <TimeInput
                                    label="وقت صلاة الفجر"
                                    hour={fajrHour}
                                    setHour={setFajrHour}
                                    minute={fajrMinute}
                                    setMinute={setFajrMinute}
                                    period={fajrPeriod}
                                    setPeriod={setFajrPeriod}
                                    timeFormat={timeFormat}
                                />
                                <hr />
                                <button
                                    className="text-white bg-success w-100"
                                    style={{ fontSize: "20px" }}
                                    onClick={calculateTahajjudTime}
                                    disabled={isLoading}
                                >
                                    {isLoading ? "جاري الحساب..." : "احسب"}
                                </button>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <div className="p-3 mb-3 bg-success text-white">النتيجة</div>
                                {error && <div className="alert alert-danger"><b>خطأ:</b> {error}</div>}
                                {result && (
                                    <div className="alert alert-success text-center">
                                        <h5>
                                            يبدأ الثلث الأخير من الليل
                                            <br />
                                            (أفضل وقت للتهجد)
                                        </h5>
                                        <hr />
                                        <p className="fs-5">
                                            من الساعة: <b>{result.start}</b>
                                        </p>
                                        <p className="fs-5">
                                            إلى الساعة: <b>{result.end}</b> (أذان الفجر)
                                        </p>
                                    </div>
                                )}
                                {!result && !error && (
                                    <div className="alert alert-info">
                                        ستظهر النتائج هنا بعد إدخال الأوقات والضغط على زر "احسب".
                                    </div>
                                )}
                            </div>
                        </div>
                        <hr />
                        <div className="row">
                            <RelatedLinks />
                            <hr />
                        </div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-4 mx-auto">
                    <MainCalculatorsList />
                </div>
            </div>
            <br />
            <div className="row">
                <div className="col-12 col-md-8" >
                    <div className="info-sections-container">
                        <div className="info-section">
                            <div className="info-section-header">
                                كيفية حساب وقت صلاة التهجد
                            </div>
                            <div className="info-section-body">
                                <u>
                                    <b> حساب الثلث الأخير من الليل </b>
                                </u>
                                <br/>                                <p>
                                    صلاة التهجد من النوافل المستحبة التي تُصلى في الليل. وأفضل
                                    وقت لأدائها هو الثلث الأخير من الليل، حيث يتجلى الله سبحانه
                                    وتعالى إلى السماء الدنيا.
                                </p>
                                <p>لحساب هذا الوقت المبارك، تتبع الحاسبة الخطوات التالية:</p>
                                <ol>
                                    <li>
                                        <b>تحديد مدة الليل:</b> يتم حساب المدة الإجمالية بالدقائق
                                        بين وقت أذان المغرب ووقت أذان الفجر.
                                    </li>
                                    <li>
                                        <b>تقسيم الليل:</b> يتم تقسيم هذه المدة على ثلاثة أجزاء
                                        متساوية لتحديد مدة كل ثلث من الليل.
                                    </li>
                                    <li>
                                        <b>تحديد الثلث الأخير:</b> يبدأ الثلث الأخير من الليل عن
                                        طريق طرح مدة الثلث الواحد من وقت الفجر، ويمتد حتى أذان
                                        الفجر.
                                    </li>
                                </ol>
                                <p>
                                    <b>مثال:</b> إذا كان المغرب الساعة 6:00 مساءً والفجر الساعة
                                    4:30 صباحًا، فإن مدة الليل هي 10 ساعات و 30 دقيقة (630 دقيقة).
                                    مدة الثلث الواحد هي 3 ساعات و 30 دقيقة (210 دقيقة). بطرح هذه
                                    المدة من وقت الفجر (4:30 صباحًا)، نجد أن الثلث الأخير يبدأ
                                    الساعة 1:00 صباحًا.
                                </p>
                                <br />
                                <hr />
                                <b>تاريخ اخر مراجعة : {new Date().toLocaleDateString('ar-EG')}</b>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <br />
        </div>
    );
}
export default TahajjudCalculator;